import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/AdminLayout';
import { Headphones, Clock, CheckCircle, AlertCircle, Search, MessageSquare, Send, Trash2, XCircle, Loader2, UserCheck, ChevronDown, User } from 'lucide-react';
import { API } from '@/App';
import { toast } from 'sonner';

const STATUS_OPTIONS = [
  { value: 'open', label: 'Ouvert', color: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  { value: 'in_progress', label: 'En cours', color: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  { value: 'resolved', label: 'Résolu', color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  { value: 'closed', label: 'Fermé', color: 'bg-gray-100 text-gray-600', dot: 'bg-gray-400' },
];

const PRIORITY_LABELS = { high: 'Haute', medium: 'Moyenne', low: 'Basse', critical: 'Critique' };
const PRIORITY_COLORS = { high: 'bg-red-100 text-red-700', medium: 'bg-amber-100 text-amber-700', low: 'bg-gray-100 text-gray-600', critical: 'bg-red-200 text-red-800' };
const CATEGORY_LABELS = { general: 'Général', account: 'Compte', technical: 'Technique', content: 'Contenu', bug: 'Bug', other: 'Autre' };

const SupportTicketsAdminPage = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [msgText, setMsgText] = useState('');
  const [sending, setSending] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [showAssignDropdown, setShowAssignDropdown] = useState(false);
  const messagesEndRef = useRef(null);
  const assignRef = useRef(null);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const fetchTickets = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tickets`, { headers });
      setTickets(res.data);
    } catch { toast.error('Erreur chargement tickets'); }
    setLoading(false);
  }, []);

  const fetchAdmins = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/users/search-for-bureau?q=`, { headers });
      const adminList = res.data.filter(u => ['super_admin', 'maintenance'].includes(u.role));
      setAdmins(adminList);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetchTickets();
    fetchAdmins();
  }, [fetchTickets, fetchAdmins]);

  // Scroll to bottom of messages when selected ticket changes
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [selected?.messages?.length, selected?.id]);

  // Close assign dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (assignRef.current && !assignRef.current.contains(e.target)) {
        setShowAssignDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const updateTicket = async (ticketId, data) => {
    try {
      await axios.put(`${API}/tickets/${ticketId}`, data, { headers });
      toast.success('Ticket mis à jour');
      await fetchTickets();
      // Refresh selected ticket
      const updatedTickets = (await axios.get(`${API}/tickets`, { headers })).data;
      const refreshed = updatedTickets.find(t => t.id === ticketId);
      if (refreshed) setSelected(refreshed);
    } catch { toast.error('Erreur mise à jour'); }
  };

  const deleteTicket = async (ticketId) => {
    if (!window.confirm('Supprimer ce ticket ?')) return;
    try {
      await axios.delete(`${API}/tickets/${ticketId}`, { headers });
      toast.success('Ticket supprimé');
      setSelected(null);
      fetchTickets();
    } catch { toast.error('Erreur suppression'); }
  };

  const sendMessage = async () => {
    if (!msgText.trim() || !selected) return;
    setSending(true);
    try {
      await axios.post(`${API}/tickets/${selected.id}/messages`, { content: msgText }, { headers });
      setMsgText('');
      // Refresh ticket data to get updated messages
      const res = await axios.get(`${API}/tickets`, { headers });
      setTickets(res.data);
      const refreshed = res.data.find(t => t.id === selected.id);
      if (refreshed) setSelected(refreshed);
      toast.success('Message envoyé');
    } catch { toast.error('Erreur envoi message'); }
    setSending(false);
  };

  const assignTicket = async (admin) => {
    const assigneeName = `${admin.first_name || ''} ${admin.last_name || ''}`.trim();
    await updateTicket(selected.id, { assignee: admin.id, assignee_name: assigneeName });
    setShowAssignDropdown(false);
  };

  const unassignTicket = async () => {
    await updateTicket(selected.id, { assignee: '', assignee_name: '' });
    setShowAssignDropdown(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const selectTicket = (t) => {
    setSelected(t);
    setMsgText('');
  };

  const filtered = tickets.filter(t => {
    if (filter !== 'all' && t.status !== filter) return false;
    if (search) {
      const s = search.toLowerCase();
      return t.title?.toLowerCase().includes(s) || t.reporter_name?.toLowerCase().includes(s) || t.reporter_email?.toLowerCase().includes(s);
    }
    return true;
  });

  const counts = {
    all: tickets.length,
    open: tickets.filter(t => t.status === 'open').length,
    in_progress: tickets.filter(t => t.status === 'in_progress').length,
    resolved: tickets.filter(t => t.status === 'resolved').length,
  };

  const formatDate = (d) => {
    try { return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return d; }
  };

  const formatTime = (d) => {
    try { return new Date(d).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }); }
    catch { return ''; }
  };

  const formatDateShort = (d) => {
    try { return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }); }
    catch { return ''; }
  };

  const isAdminRole = (role) => ['super_admin', 'maintenance'].includes(role);

  return (
    <AdminLayout>
      <div className="p-5 lg:p-8 min-h-full bg-[#f8f9fb]" style={{ fontFamily: "'Nunito', sans-serif" }} data-testid="support-admin-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#1e3a5f]" style={{ fontFamily: "'Barlow', sans-serif" }}>Support Technique</h1>
            <p className="text-sm text-gray-400 mt-1">Gestion avancée des tickets de support</p>
          </div>
          <div className="w-10 h-10 rounded-xl bg-[#eaf6fc] flex items-center justify-center">
            <Headphones size={18} className="text-[#35AFE4]" />
          </div>
        </div>

        {/* Counters */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          {[
            { key: 'all', label: 'Total', count: counts.all, icon: MessageSquare, color: 'bg-blue-100 text-blue-600' },
            { key: 'open', label: 'Ouverts', count: counts.open, icon: Clock, color: 'bg-amber-100 text-amber-600' },
            { key: 'in_progress', label: 'En cours', count: counts.in_progress, icon: AlertCircle, color: 'bg-violet-100 text-violet-600' },
            { key: 'resolved', label: 'Résolus', count: counts.resolved, icon: CheckCircle, color: 'bg-emerald-100 text-emerald-600' },
          ].map(s => (
            <button key={s.key} onClick={() => setFilter(s.key)}
              className={`p-4 rounded-2xl border transition-all text-left ${filter === s.key ? 'bg-white border-[#35AFE4] shadow-sm' : 'bg-white border-gray-100 hover:border-gray-200'}`}
              data-testid={`filter-${s.key}`}>
              <div className={`w-8 h-8 rounded-lg ${s.color} flex items-center justify-center mb-2`}>
                <s.icon size={14} />
              </div>
              <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400">{s.label}</p>
              <p className="text-xl font-bold text-gray-900">{s.count}</p>
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher un ticket..."
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#35AFE4] focus:border-transparent outline-none bg-white" data-testid="search-input" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
          {/* Ticket list */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden" data-testid="tickets-list">
              {loading ? (
                <div className="flex justify-center py-12"><Loader2 size={24} className="animate-spin text-[#35AFE4]" /></div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle size={28} className="mx-auto mb-2 text-gray-200" />
                  <p className="text-sm text-gray-400">Aucun ticket</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-50 max-h-[600px] overflow-y-auto">
                  {filtered.map(t => {
                    const st = STATUS_OPTIONS.find(s => s.value === t.status) || STATUS_OPTIONS[0];
                    const isActive = selected?.id === t.id;
                    const msgCount = t.messages?.length || 0;
                    return (
                      <button key={t.id} onClick={() => selectTicket(t)}
                        className={`w-full p-4 text-left hover:bg-gray-50 transition-colors ${isActive ? 'bg-[#f4fafe] border-l-2 border-l-[#35AFE4]' : ''}`}
                        data-testid={`ticket-item-${t.id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">{t.title}</p>
                            <p className="text-[11px] text-gray-400 mt-0.5">{t.reporter_name} — {formatDate(t.created_at)}</p>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            {msgCount > 0 && (
                              <span className="text-[10px] bg-[#eaf6fc] text-[#35AFE4] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                                <MessageSquare size={9} /> {msgCount}
                              </span>
                            )}
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${st.color}`}>{st.label}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${PRIORITY_COLORS[t.priority] || ''}`}>
                            {PRIORITY_LABELS[t.priority] || t.priority}
                          </span>
                          <span className="text-[10px] text-gray-400">{CATEGORY_LABELS[t.category] || t.category}</span>
                          {t.assignee_name && (
                            <span className="text-[10px] text-[#35AFE4] flex items-center gap-0.5 ml-auto">
                              <UserCheck size={10} /> {t.assignee_name}
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Ticket detail */}
          <div className="lg:col-span-3" data-testid="ticket-detail">
            {!selected ? (
              <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
                <MessageSquare size={36} className="mx-auto mb-3 text-gray-200" />
                <p className="text-sm text-gray-400">Sélectionnez un ticket pour voir les détails</p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-gray-100 flex flex-col" style={{ maxHeight: '720px' }}>
                {/* Ticket header */}
                <div className="p-5 border-b border-gray-100">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h2 className="text-lg font-bold text-[#1e3a5f] truncate" style={{ fontFamily: "'Barlow', sans-serif" }}>{selected.title}</h2>
                      <p className="text-xs text-gray-400 mt-1">
                        Par <span className="font-semibold text-gray-600">{selected.reporter_name}</span> ({selected.reporter_email}) — {formatDate(selected.created_at)}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => deleteTicket(selected.id)} className="p-2 text-gray-300 hover:text-red-500 transition-colors" data-testid="delete-ticket-btn" title="Supprimer">
                        <Trash2 size={16} />
                      </button>
                      <button onClick={() => setSelected(null)} className="p-2 text-gray-300 hover:text-gray-500 transition-colors" title="Fermer">
                        <XCircle size={16} />
                      </button>
                    </div>
                  </div>

                  {/* Metadata row: priority, category, assignee, status */}
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${PRIORITY_COLORS[selected.priority] || ''}`}>
                      {PRIORITY_LABELS[selected.priority] || selected.priority}
                    </span>
                    <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-gray-100 text-gray-600">
                      {CATEGORY_LABELS[selected.category] || selected.category}
                    </span>

                    {/* Assignee dropdown */}
                    <div className="relative ml-auto" ref={assignRef}>
                      <button onClick={() => setShowAssignDropdown(!showAssignDropdown)}
                        className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-gray-200 hover:border-[#35AFE4] transition-colors"
                        data-testid="assign-dropdown-btn">
                        <UserCheck size={12} className="text-[#35AFE4]" />
                        <span className="text-gray-600">{selected.assignee_name || 'Non assigné'}</span>
                        <ChevronDown size={12} className="text-gray-400" />
                      </button>
                      {showAssignDropdown && (
                        <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-xl border border-gray-200 shadow-lg z-20 py-1 max-h-48 overflow-y-auto" data-testid="assign-dropdown-menu">
                          <button onClick={unassignTicket}
                            className="w-full px-3 py-2 text-left text-xs text-gray-400 hover:bg-gray-50 flex items-center gap-2">
                            <XCircle size={12} /> Retirer l'assignation
                          </button>
                          {admins.map(a => (
                            <button key={a.id} onClick={() => assignTicket(a)}
                              className={`w-full px-3 py-2 text-left text-xs hover:bg-[#f4fafe] flex items-center gap-2 ${selected.assignee === a.id ? 'bg-[#f4fafe] text-[#35AFE4] font-semibold' : 'text-gray-700'}`}
                              data-testid={`assign-option-${a.id}`}>
                              {a.profile_image ? (
                                <img src={a.profile_image} alt="" className="w-5 h-5 rounded-full object-cover" />
                              ) : (
                                <div className="w-5 h-5 rounded-full bg-[#eaf6fc] flex items-center justify-center">
                                  <User size={10} className="text-[#35AFE4]" />
                                </div>
                              )}
                              {a.first_name} {a.last_name}
                              <span className="text-[10px] text-gray-400 ml-auto">{a.role === 'super_admin' ? 'Admin' : 'Maint.'}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Status buttons */}
                  <div className="flex gap-1.5 mt-3">
                    {STATUS_OPTIONS.map(s => (
                      <button key={s.value} onClick={() => updateTicket(selected.id, { status: s.value })}
                        className={`px-3 py-1 text-[11px] font-semibold rounded-full border transition-all ${selected.status === s.value ? s.color + ' border-current' : 'bg-white border-gray-200 text-gray-400 hover:border-gray-300'}`}
                        data-testid={`status-${s.value}`}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Conversation thread */}
                <div className="flex-1 overflow-y-auto p-5 space-y-4 bg-[#fafbfc]" style={{ minHeight: '280px' }} data-testid="messages-thread">
                  {/* Initial ticket description as first "message" */}
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <User size={14} className="text-gray-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-bold text-gray-800">{selected.reporter_name}</span>
                        <span className="text-[10px] text-gray-400">{formatDateShort(selected.created_at)} à {formatTime(selected.created_at)}</span>
                        <span className="text-[9px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-medium">Demande initiale</span>
                      </div>
                      <div className="p-3.5 bg-white border border-gray-100 rounded-xl rounded-tl-sm text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                        {selected.description}
                      </div>
                    </div>
                  </div>

                  {/* Legacy response (if exists from old system) */}
                  {selected.response && (!selected.messages || selected.messages.length === 0) && (
                    <div className="flex gap-3 justify-end">
                      <div className="flex-1 min-w-0 max-w-[85%]">
                        <div className="flex items-center gap-2 mb-1 justify-end">
                          <span className="text-[10px] text-gray-400">Réponse admin</span>
                          <span className="text-xs font-bold text-[#35AFE4]">Admin</span>
                        </div>
                        <div className="p-3.5 bg-[#1e3a5f] text-white rounded-xl rounded-tr-sm text-sm leading-relaxed whitespace-pre-wrap">
                          {selected.response}
                        </div>
                      </div>
                      <div className="w-8 h-8 rounded-full bg-[#1e3a5f] flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Headphones size={14} className="text-white" />
                      </div>
                    </div>
                  )}

                  {/* Threaded messages */}
                  {(selected.messages || []).map((msg) => {
                    const isAdmin = isAdminRole(msg.sender_role);
                    return (
                      <div key={msg.id} className={`flex gap-3 ${isAdmin ? 'justify-end' : ''}`}>
                        {!isAdmin && (
                          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <User size={14} className="text-gray-500" />
                          </div>
                        )}
                        <div className={`flex-1 min-w-0 ${isAdmin ? 'max-w-[85%]' : ''}`}>
                          <div className={`flex items-center gap-2 mb-1 ${isAdmin ? 'justify-end' : ''}`}>
                            {!isAdmin && <span className="text-xs font-bold text-gray-800">{msg.sender_name}</span>}
                            <span className="text-[10px] text-gray-400">{formatDateShort(msg.created_at)} à {formatTime(msg.created_at)}</span>
                            {isAdmin && <span className="text-xs font-bold text-[#35AFE4]">{msg.sender_name}</span>}
                          </div>
                          <div className={`p-3.5 rounded-xl text-sm leading-relaxed whitespace-pre-wrap ${
                            isAdmin
                              ? 'bg-[#1e3a5f] text-white rounded-tr-sm'
                              : 'bg-white border border-gray-100 text-gray-700 rounded-tl-sm'
                          }`}>
                            {msg.content}
                          </div>
                        </div>
                        {isAdmin && (
                          <div className="w-8 h-8 rounded-full bg-[#1e3a5f] flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Headphones size={14} className="text-white" />
                          </div>
                        )}
                      </div>
                    );
                  })}

                  <div ref={messagesEndRef} />
                </div>

                {/* Message input */}
                <div className="p-4 border-t border-gray-100 bg-white rounded-b-2xl">
                  <div className="flex gap-2">
                    <textarea
                      value={msgText}
                      onChange={e => setMsgText(e.target.value)}
                      onKeyDown={handleKeyDown}
                      rows={2}
                      placeholder="Tapez votre message... (Entrée pour envoyer)"
                      className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#35AFE4] focus:border-transparent outline-none resize-none"
                      data-testid="message-input"
                    />
                    <button
                      onClick={sendMessage}
                      disabled={sending || !msgText.trim()}
                      className="px-4 py-2.5 bg-[#1e3a5f] hover:bg-[#2a5a8f] text-white rounded-xl transition-colors flex items-center justify-center disabled:opacity-40 self-end"
                      data-testid="send-message-btn"
                    >
                      {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default SupportTicketsAdminPage;
